import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Eye, EyeOff, Image, Tag } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';

interface PostFormData {
  title: string;
  content: string;
  excerpt: string;
  category: string;
  tags: string[];
  isPublished: boolean;
  isFeatured: boolean;
  featuredImage: string;
}

const PostForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();
  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());

  const [formData, setFormData] = useState<PostFormData>({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: [],
    isPublished: false,
    isFeatured: false,
    featuredImage: ''
  });

  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const categories = [
    { value: 'imigração', labelKey: 'blogCategories.immigration' },
    { value: 'documentos', labelKey: 'blogCategories.documents' },
    { value: 'vistos', labelKey: 'blogCategories.visas' },
    { value: 'cidadania', labelKey: 'blogCategories.citizenship' },
    { value: 'trabalho', labelKey: 'blogCategories.work' },
    { value: 'estudos', labelKey: 'blogCategories.studies' },
    { value: 'geral', labelKey: 'blogCategories.general' }
  ];

  const fetchPost = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/posts/${id}`);
      const post = response.data;
      setFormData({
        title: post.title || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        category: post.category || '',
        tags: post.tags || [],
        isPublished: post.isPublished || false,
        isFeatured: post.isFeatured || false,
        featuredImage: post.featuredImage || ''
      });
      setI18n({
        pt: {
          title: post.i18n?.pt?.title || post.title || '',
          content: post.i18n?.pt?.content || post.content || '',
          excerpt: post.i18n?.pt?.excerpt || post.excerpt || ''
        },
        en: {
          title: post.i18n?.en?.title || '',
          content: post.i18n?.en?.content || '',
          excerpt: post.i18n?.en?.excerpt || ''
        },
        es: {
          title: post.i18n?.es?.title || '',
          content: post.i18n?.es?.content || '',
          excerpt: post.i18n?.es?.excerpt || ''
        }
      });
    } catch (error) {
      console.error('Erro ao carregar post:', error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing && id) {
      fetchPost();
    }
  }, [isEditing, id, fetchPost]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validações do frontend
    const ptTitle = i18n.pt?.title?.trim() || '';
    const ptContent = i18n.pt?.content?.trim() || '';

    if (ptTitle.length < 5) {
      notifications.warning.validation(t('admin.postTitleMin'));
      setLoading(false);
      return;
    }

    if (ptContent.length < 50) {
      notifications.warning.validation(t('admin.postContentMin'));
      setLoading(false);
      return;
    }

    if (!formData.category) {
      notifications.warning.validation(t('admin.selectCategory'));
      setLoading(false);
      return;
    }

    const payload = {
      ...formData,
      title: ptTitle,
      content: ptContent,
      excerpt: i18n.pt?.excerpt?.trim() || formData.excerpt,
      i18n
    };

    try {
      if (isEditing) {
        await axios.put(`/posts/${id}`, payload);
        notifications.success.postUpdated();
      } else {
        await axios.post('/posts', payload);
        notifications.success.postCreated();
      }

      // Aguardar um pouco para mostrar a notificação antes de navegar
      setTimeout(() => {
        navigate('/admin/posts');
      }, 1500);
    } catch (error: any) {
      console.error('Erro ao salvar post:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        notifications.error.postCreate(errorMessages);
      } else {
        notifications.error.postCreate(t('common.postSaveError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/posts')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isEditing ? t('admin.postEdit') : t('admin.postNew')}
                </h1>
                <p className="text-gray-600">
                  {isEditing ? t('admin.postEditDesc') : t('admin.postNewDesc')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setPreviewMode(!previewMode)}
                className="btn-secondary flex items-center space-x-2"
              >
                {previewMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>{previewMode ? t('admin.editMode') : t('admin.preview')}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <p className="text-sm text-gray-500">{t('admin.i18nHint')}</p>
              <I18nTripleFields mode="post" value={i18n} onChange={setI18n} />

              {/* Featured Image */}
              <div>
                <label htmlFor="featuredImage" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.featuredImage')}
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="url"
                    id="featuredImage"
                    name="featuredImage"
                    value={formData.featuredImage}
                    onChange={handleInputChange}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('common.imageUrlPh')}
                  />
                  <button
                    type="button"
                    className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Image className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/posts')}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>
                    {loading
                      ? t('common.saving')
                      : isEditing
                        ? t('common.updating')
                        : t('common.createPost')}
                  </span>
                </button>
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('common.settings')}</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label htmlFor="isPublished" className="text-sm font-medium text-gray-700">
                    {t('admin.published')}
                  </label>
                  <input
                    type="checkbox"
                    id="isPublished"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label htmlFor="isFeatured" className="text-sm font-medium text-gray-700">
                    {t('admin.featured')}
                  </label>
                  <input
                    type="checkbox"
                    id="isFeatured"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.category')}</h3>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">{t('common.selectCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {t(cat.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.tags')}</h3>
              
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('common.addTagPh')}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Tag className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostForm;
